import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { QueryClient } from "react-query";
import { toast } from "react-toastify";

import { GeoMapFieldTemplatesQueryKey } from "../../hooks/useFetchFieldTemplates";
import { MapKind } from "../../hooks/useMakeMap";
import { DuplicateLayerNameError } from "../../hooks/usePostMapLayer";
import { Calculation, Field, FieldTemplate, Raster } from "../../models/mapLayer";
import { Layer, PreservedUploadState, RasterWithFile } from "../GeoMapLoaderModal.types";
import { processRasterFiles } from "./processRasterFiles";

interface UseCreateModeHandlersProps {
  layers: Layer[];
  setLayers: React.Dispatch<React.SetStateAction<Layer[]>>;
  layerGroupName: string;
  selectedCalculationMapTemplate: string;
  setSelectedCalculationMapTemplate: React.Dispatch<React.SetStateAction<string>>;
  settings: { getAllSettings: () => Field[] } | undefined;
  calculationFieldTemplates: FieldTemplate[] | undefined;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setDuplicateNameError: React.Dispatch<React.SetStateAction<string | null>>;
  setShowTemplateNameModal: React.Dispatch<React.SetStateAction<boolean>>;
  newlyAddedLayerId: string | null;
  setNewlyAddedLayerId: React.Dispatch<React.SetStateAction<string | null>>;
  isEditMode: boolean;
  editableLayersCount: number;
  autoCloseTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  autoCloseDelayMs: number;
  handleClose: () => void;
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
  onUploadProgress?: (progress: number) => void;
  onUploadError?: (preservedState: PreservedUploadState) => void;
  setBackgroundUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  // API functions
  addMapLayer: (params: {
    name: string;
    projectId: string | null;
    group: string;
    metadata: null;
    maps: [];
  }) => Promise<number | undefined>;
  uploadMap: (params: {
    id: number;
    file: File | null;
    name: string;
    expression: string;
    projFile?: File;
    unit: string;
    min?: number;
    max?: number;
    overrideFileName: string;
  }) => Promise<string>;
  makeMap: (params: { id: number; kind: MapKind }) => Promise<unknown>;
  refetchGeoMapLayers: () => Promise<void>;
  addNewFieldTemplateAsync: (template: FieldTemplate) => Promise<string>;
  deleteFieldTemplate: (params: { id: string }) => Promise<void>;
  deleteMapLayer: (params: { id: number }) => Promise<void>;
  queryClient: QueryClient;
}

export function useCreateModeHandlers({
  layers,
  setLayers,
  layerGroupName,
  selectedCalculationMapTemplate,
  setSelectedCalculationMapTemplate,
  settings,
  calculationFieldTemplates,
  setIsUploading,
  setIsSaving,
  setDuplicateNameError,
  setShowTemplateNameModal,
  newlyAddedLayerId,
  setNewlyAddedLayerId,
  isEditMode,
  editableLayersCount,
  autoCloseTimerRef,
  autoCloseDelayMs,
  handleClose,
  onUploadingChange,
  onUploadProgress,
  onUploadError,
  setBackgroundUploadError,
  addMapLayer,
  uploadMap,
  makeMap,
  refetchGeoMapLayers,
  addNewFieldTemplateAsync,
  deleteFieldTemplate,
  deleteMapLayer,
  queryClient
}: UseCreateModeHandlersProps) {
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const onUploadProgressRef = useRef(onUploadProgress);
  onUploadProgressRef.current = onUploadProgress;

  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;

  const handleFilesSelected = async (layerId: string, files: File[]) => {
    setIsUploading(true);
    try {
      const newMaps = processRasterFiles(files, settings?.getAllSettings() ?? []);

      setLayers((prev) =>
        prev.map((layer) =>
          layer.id === layerId ? { ...layer, maps: [...layer.maps, ...newMaps] } : layer
        )
      );
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Error processing files");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteMap = (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation }
  ) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId
          ? { ...layer, maps: layer.maps.filter((m) => m !== map) }
          : layer
      )
    );
  };

  // Helper function to update a specific map within a layer (create mode)
  const updateLayerMap = (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation },
    updater: (m: { Raster?: Raster; Calculation?: Calculation }) => {
      Raster?: Raster;
      Calculation?: Calculation;
    }
  ) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              maps: layer.maps.map((m) => (m === map ? updater(m) : m))
            }
          : layer
      )
    );
  };

  const handleMapChange = async (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation },
    changes: { min?: number; max?: number; unit?: string }
  ) => {
    updateLayerMap(layerId, map, (m) => {
      const updated = { ...m };
      if (updated.Raster) {
        updated.Raster = { ...updated.Raster, ...changes };
      } else if (updated.Calculation) {
        updated.Calculation = { ...updated.Calculation, ...changes };
      }
      return updated;
    });
  };

  const handleMapNameChanged = (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation },
    name: string
  ) => {
    updateLayerMap(layerId, map, (m) => {
      const updated = { ...m };
      const target = updated.Raster ?? updated.Calculation;

      if (target) {
        // Update name
        target.name = name;

        // Look up field settings and auto-populate unit, min, max, and expression
        const foundField: Field = settings?.getAllSettings().find((f) => f.name === name);
        if (foundField) {
          target.unit = foundField.unit;
          target.min = foundField.min;
          target.max = foundField.max;
          if (foundField?.expression?.length > 0) {
            target.transformer = {
              type: "ExpressionTransform",
              expression: foundField.expression
            };
          }
        }
      }

      return updated;
    });
  };

  const handleExpressionValueChanged = async (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation },
    expression: string
  ) => {
    updateLayerMap(layerId, map, (m) => {
      const updated = { ...m };
      const target = updated.Raster ?? updated.Calculation;
      if (target) {
        target.transformer = {
          type: "ExpressionTransform",
          expression
        };
      }
      return updated;
    });
  };

  const handleScaleValuesToggled = (
    layerId: string,
    map: { Raster?: Raster; Calculation?: Calculation },
    checked: boolean
  ) => {
    updateLayerMap(layerId, map, (m) => {
      if (m.Raster) {
        const updated = { ...m };
        if (checked) {
          const field = settings?.getAllSettings().find((f) => f.name === m.Raster?.name);
          const expression =
            field?.expression ?? m.Raster?.transformer?.expression ?? "$Value";
          updated.Raster.transformer = {
            type: "ExpressionTransform",
            expression
          };
        } else {
          delete updated.Raster.transformer;
        }
        return updated;
      }
      return m;
    });
  };

  // Helper function to create calculation maps from a template or single calculation
  const createCalculationMaps = (
    templateName: string
  ): Array<{ Raster?: Raster; Calculation?: Calculation }> => {
    const template = calculationFieldTemplates?.find((t) => t.name === templateName);

    if (template && templateName !== "Single") {
      // Create maps from template
      return template.fields.map((field) => ({
        Calculation: {
          name: field.name,
          id: `temp-calc-${Date.now()}-${Math.random()}`,
          filepath: "",
          createdDate: {
            $date: new Date().toISOString()
          },
          createdBy: "",
          transformer: {
            type: "ExpressionTransform" as const,
            expression: field.expression ?? "$"
          },
          unit: field.unit,
          min: field.min,
          max: field.max
        }
      }));
    }

    // Create single calculation map
    return [
      {
        Calculation: {
          name: "New Calculation",
          id: `temp-calc-${Date.now()}-${Math.random()}`,
          createdDate: {
            $date: new Date().toISOString()
          },
          createdBy: "",
          transformer: {
            type: "ExpressionTransform" as const,
            expression: "$"
          },
          unit: "",
          min: undefined,
          max: undefined
        }
      }
    ];
  };

  const handleAddNewCalculationMap = (layerId: string) => {
    const newMaps = createCalculationMaps(selectedCalculationMapTemplate);

    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, maps: [...layer.maps, ...newMaps] } : layer
      )
    );
  };

  const handleSaveCalculationMapsAsNewTemplate = () => {
    requestAnimationFrame(() => {
      // requestAnimationFrame to allow the menu to close before the modal opens
      setShowTemplateNameModal(true);
    });
  };

  const saveCalculationMapsAsNewTemplate = async (name: string) => {
    // Collect all calculation maps from all layers
    const allMaps = layers.flatMap((layer) => layer.maps);
    const calculationMaps = allMaps.filter((f) => f.Calculation);
    const fields = calculationMaps.map((m) => {
      const field = m.Calculation;
      return {
        name: field.name,
        unit: field.unit,
        expression: field.transformer?.expression,
        min: field.min,
        max: field.max
      } as Field;
    });
    const template = {
      name,
      fields
    } as FieldTemplate;
    await addNewFieldTemplateAsync(template);
    await queryClient.invalidateQueries(GeoMapFieldTemplatesQueryKey);
    setShowTemplateNameModal(false);
  };

  const handleLayerNameChange = (layerId: string, name: string) => {
    setLayers((prev) =>
      prev.map((layer) => (layer.id === layerId ? { ...layer, name } : layer))
    );
  };

  const handleDeleteSelectedTemplate = async () => {
    const found = calculationFieldTemplates?.find(
      (f) => f.name === selectedCalculationMapTemplate
    );
    if (found && found.id?.["$oid"]) {
      await deleteFieldTemplate({ id: found.id["$oid"] });
      await queryClient.invalidateQueries(GeoMapFieldTemplatesQueryKey);
      setSelectedCalculationMapTemplate("Single");
    }
  };

  const handleAddLayer = () => {
    const totalLayerCount = isEditMode
      ? editableLayersCount + layers.length
      : layers.length;
    const newLayerId = `layer-${Date.now()}-${Math.random()}`;
    const newLayer: Layer = {
      id: newLayerId,
      name: `New Zone ${totalLayerCount + 1}`,
      maps: []
    };
    setLayers((prev) => [...prev, newLayer]);
    setNewlyAddedLayerId(newLayerId);
  };

  // Delete a new layer from local state
  const handleDeleteNewLayer = (layerId: string) => {
    setLayers((prev) => prev.filter((layer) => layer.id !== layerId));
    // Clear newlyAddedLayerId if we're deleting that layer
    if (newlyAddedLayerId === layerId) {
      setNewlyAddedLayerId(null);
    }
  };

  // Helper function to process maps for a layer
  // progressBase/progressSlice allow the caller to allocate a portion of the overall progress bar
  const processLayerMaps = async (
    layer: Layer,
    layerId: number,
    progressBase = 0,
    progressSlice = 0
  ) => {
    const rasterMaps = layer.maps.filter((m) => m.Raster);
    const calculationMaps = layer.maps.filter((m) => m.Calculation);
    const totalFiles = rasterMaps.length + calculationMaps.length;
    // Within the layer slice: file uploads get 25%, make-map gets 50%, make-lsd-map gets 25%
    const fileSlice = progressSlice * 0.25;
    const makeMapPoint = progressBase + progressSlice * 0.75;
    const makeLsdPoint = progressBase + progressSlice;
    let filesUploaded = 0;

    for (const map of rasterMaps) {
      const raster = map.Raster as RasterWithFile;
      if (raster._file) {
        await uploadMap({
          id: layerId,
          file: raster._file,
          name: raster.name,
          expression: raster.transformer?.expression ?? "",
          projFile: raster._projFile,
          unit: raster.unit ?? "",
          min: raster.min,
          max: raster.max,
          overrideFileName: raster._overrideFileName ?? ""
        });
        filesUploaded++;
        if (totalFiles > 0 && progressSlice > 0) {
          onUploadProgressRef.current?.(
            Math.round(progressBase + (filesUploaded / totalFiles) * fileSlice)
          );
        }
      }
    }

    // Upload all Calculation maps
    for (const map of calculationMaps) {
      const calc = map.Calculation;
      await uploadMap({
        id: layerId,
        file: null,
        name: calc.name,
        expression: calc.transformer?.expression ?? "$",
        unit: calc.unit ?? "",
        min: calc.min,
        max: calc.max,
        overrideFileName: ""
      });
      filesUploaded++;
      if (totalFiles > 0 && progressSlice > 0) {
        onUploadProgressRef.current?.(
          Math.round(progressBase + (filesUploaded / totalFiles) * fileSlice)
        );
      }
    }

    await makeMap({ id: layerId, kind: MapKind.Native });
    if (progressSlice > 0) {
      onUploadProgressRef.current?.(Math.round(makeMapPoint));
    }

    await makeMap({ id: layerId, kind: MapKind.LSD });
    if (progressSlice > 0) {
      onUploadProgressRef.current?.(Math.round(makeLsdPoint));
    }
  };

  const handleOk = async () => {
    const totalMaps = layers.reduce((sum, layer) => sum + layer.maps.length, 0);
    if (totalMaps === 0) {
      toast.error("Please add at least one map");
      return;
    }

    setDuplicateNameError(null);
    setBackgroundUploadError(null);
    setIsSaving(true);

    // Notify parent that uploading has started
    onUploadingChangeRef.current?.(true);

    // Capture current state for both processing and potential recovery
    const currentLayers = layers.map((layer) => ({ ...layer, maps: [...layer.maps] }));
    const currentLayerGroupName = layerGroupName;
    const currentSelectedTemplate = selectedCalculationMapTemplate;

    // Snapshot state for recovery in case of background failure
    const errorMessage = "Upload failed. Please review your layers and try again.";
    const preservedState: PreservedUploadState = {
      layers: currentLayers,
      editableLayers: [],
      layerGroupName: currentLayerGroupName,
      selectedCalculationMapTemplate: currentSelectedTemplate,
      isEditMode: false,
      errorMessage
    };

    // Find the first layer with maps to create first (to validate group name)
    const firstLayerWithMaps = currentLayers.find((layer) => layer.maps.length > 0);
    if (!firstLayerWithMaps) {
      setIsSaving(false);
      onUploadingChangeRef.current?.(false);
      return;
    }

    try {
      // Try to create the first layer synchronously to catch duplicate name errors
      const firstLayerId = await addMapLayer({
        name: firstLayerWithMaps.name,
        projectId: null,
        group: currentLayerGroupName,
        metadata: null,
        maps: []
      });

      if (!firstLayerId) {
        throw new Error("Failed to get layer ID from response");
      }

      // Report initial progress after first layer created
      onUploadProgressRef.current?.(10);

      // First layer created successfully - close modal and continue in background
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
      }, autoCloseDelayMs);

      // Continue processing in background
      (async () => {
        // Track all created layer IDs for cleanup on failure
        const createdLayerIds: number[] = [firstLayerId];

        // Calculate progress slices: each layer gets an equal portion from 10% to 90%
        const layersWithMaps = currentLayers.filter((l) => l.maps.length > 0);
        const numLayers = layersWithMaps.length;
        const slicePerLayer = 80 / numLayers; // 80% total (10 to 90)

        try {
          // Process the first layer's maps
          await processLayerMaps(firstLayerWithMaps, firstLayerId, 10, slicePerLayer);

          // Process remaining layers
          let layerIndex = 1;
          for (const layer of currentLayers) {
            if (layer.id === firstLayerWithMaps.id || layer.maps.length === 0) {
              continue; // Skip first layer (already processed) and empty layers
            }

            const layerId = await addMapLayer({
              name: layer.name,
              projectId: null,
              group: currentLayerGroupName,
              metadata: null,
              maps: []
            });

            if (!layerId) {
              throw new Error("Failed to get layer ID from response");
            }

            createdLayerIds.push(layerId);
            const layerBase = 10 + layerIndex * slicePerLayer;
            await processLayerMaps(layer, layerId, layerBase, slicePerLayer);
            layerIndex++;
          }

          // Reset uploading state BEFORE refetching so treeData rebuilds with correct state
          onUploadingChangeRef.current?.(false);

          // Refresh the geo map layers list
          await refetchGeoMapLayers();

          toast.success("Layer map(s) created successfully!");
        } catch (error) {
          Sentry.captureException(error);

          // Clear the auto-close timer so it doesn't close the modal after we reopen it
          if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
          }

          // Clean up: delete all layers that were created before the failure
          // This allows the user to resubmit without hitting duplicate name errors
          for (const layerId of createdLayerIds) {
            try {
              await deleteMapLayer({ id: layerId });
            } catch (deleteError) {
              Sentry.captureException(deleteError);
            }
          }

          // Refresh to ensure UI reflects the cleanup
          await refetchGeoMapLayers();

          // Show error and call error callback to restore state and reopen modal
          setBackgroundUploadError(errorMessage);
          if (onUploadErrorRef.current) {
            onUploadErrorRef.current(preservedState);
          }
        } finally {
          onUploadingChangeRef.current?.(false);
        }
      })();
    } catch (error) {
      // Check if this is a duplicate name error
      if (error instanceof DuplicateLayerNameError) {
        setDuplicateNameError(error.message);
        setIsSaving(false);
        onUploadingChangeRef.current?.(false);
        return;
      }

      // Other errors
      Sentry.captureException(error);
      setBackgroundUploadError("Error creating layer map. Please try again.");
      setIsSaving(false);
      onUploadingChangeRef.current?.(false);
    }
  };

  return {
    handleFilesSelected,
    handleDeleteMap,
    handleMapChange,
    handleMapNameChanged,
    handleExpressionValueChanged,
    handleScaleValuesToggled,
    handleAddNewCalculationMap,
    handleSaveCalculationMapsAsNewTemplate,
    saveCalculationMapsAsNewTemplate,
    handleLayerNameChange,
    handleDeleteSelectedTemplate,
    handleAddLayer,
    handleDeleteNewLayer,
    handleOk,
    createCalculationMaps,
    processLayerMaps
  };
}
